import { DAMAGE_LIST_TABLE_ID } from 'components/DamageTable2/utils';
import { useAccountContext } from 'utils/account/AccountContext';
import { useUserTablePreferences } from 'utils/account/useUserTablePreferences';
import { SKYSPECS_ORG } from 'utils/constants';
import { flags, useFeatures } from 'utils/features';

import {
  canManageDamagesTableNoPreLoading,
  toShouldDeferUntilFiltered,
} from './damagesTablePreLoading';

export const useDamagesTablePreLoading = () => {
  const { customer } = useAccountContext();
  const { features } = useFeatures();

  const customerId = customer?.id;
  // Flag name is fixed backend-side: ON means default to defer (No Pre-Loading).
  const orgFeatureFlagEnabled = Boolean(features?.[flags.DAMAGES_2_NO_PRE_LOADING]);
  const isSkySpecsOrg = customerId === SKYSPECS_ORG;
  const {
    tablePreferences,
    loading: tablePreferenceLoading,
    error: tablePreferenceError,
    refetch: refetchTablePreference,
  } = useUserTablePreferences({
    tableId: DAMAGE_LIST_TABLE_ID,
    skip: !customerId || isSkySpecsOrg,
  });

  // Query failure with no payload is not "no override". Cached or partial data
  // still drives the table. A missing payload must not fall back to the org-flag
  // default — that default can permit preloading and would fire the unfiltered
  // Damages list even if the user had disabled it.
  const isTablePreferenceUnavailable =
    Boolean(customerId) &&
    !isSkySpecsOrg &&
    Boolean(tablePreferenceError) &&
    tablePreferences === undefined;

  // SkySpecs always defers until a filter is applied, with no user toggle.
  // `true` = defer query (No Pre-Loading), never "pre-load is on".
  // Unavailable preference: fail-safe to defer. Scoped/filtered pages still
  // query via hasScopingUniversalFilters; retry remains on the error banner.
  const shouldDeferUntilFiltered =
    isSkySpecsOrg || isTablePreferenceUnavailable
      ? true
      : toShouldDeferUntilFiltered({
          orgFeatureFlagEnabled,
          preloading: tablePreferences?.preloading,
        });

  return {
    isSkySpecsOrg,
    orgFeatureFlagEnabled,
    /**
     * SkySpecs has no user toggle. Every other org can opt in/out in Settings;
     * the org feature flag only supplies the default when the API has no override.
     */
    canManageNoPreLoading: canManageDamagesTableNoPreLoading(customerId),
    shouldDeferUntilFiltered,
    // Keep Damages 2 from firing an unfiltered list while this table's preference loads.
    isTablePreferenceLoading: Boolean(customerId) && !isSkySpecsOrg && tablePreferenceLoading,
    isTablePreferenceUnavailable,
    refetchTablePreference,
  };
};
